import { useState } from "react"
import { ArrowsUp, ChevronDown, ChevronUp } from "@untitledui/icons"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { cx } from "@/lib/utils/cx"

// Replaces click-to-sort on table headers with a discoverable, named control
// that non-technical users can find and use. Click an inactive field to make
// it active (defaults to descending — "newest first" / "highest first" /
// "Z to A" — which is what users almost always want). Click the active
// field again to flip its direction.

export type SortField = "date" | "customer" | "vehicle" | "amount"
export type SortDirection = "asc" | "desc"

interface FieldDef {
  key: SortField
  label: string
  descLabel: string
  ascLabel: string
}

const FIELDS: FieldDef[] = [
  { key: "date", label: "Date", descLabel: "Newest first", ascLabel: "Oldest first" },
  { key: "customer", label: "Customer", descLabel: "Z to A", ascLabel: "A to Z" },
  { key: "vehicle", label: "Vehicle", descLabel: "Z to A", ascLabel: "A to Z" },
  { key: "amount", label: "Estimate amount", descLabel: "Highest first", ascLabel: "Lowest first" },
]

interface SortPillProps {
  field: SortField
  direction: SortDirection
  onChange: (field: SortField, direction: SortDirection) => void
}

export function SortPill({ field, direction, onChange }: SortPillProps) {
  const [open, setOpen] = useState(false)
  const current = FIELDS.find((f) => f.key === field) ?? FIELDS[0]
  const summary = direction === "desc" ? current.descLabel : current.ascLabel

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cx(
            "h-10 px-3 rounded-xl border border-zinc-200 bg-zinc-50 text-xs md:text-sm font-medium flex items-center gap-2 shrink-0",
            "hover:border-zinc-300 hover:bg-white transition-all duration-150",
            "data-[state=open]:bg-white data-[state=open]:border-zinc-300",
          )}
        >
          <ArrowsUp className="size-3.5 text-zinc-400" aria-hidden />
          <span className="text-zinc-500">Sort</span>
          <span className="font-semibold text-zinc-900 truncate max-w-[110px]">{summary}</span>
          <ChevronDown className="size-3.5 text-zinc-400 shrink-0" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className="w-[260px] p-0 rounded-2xl border border-zinc-200/70 bg-white shadow-2xl shadow-zinc-900/15 overflow-hidden"
      >
        <div className="px-4 py-2.5 border-b border-zinc-100">
          <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-zinc-500">
            Sort by
          </p>
        </div>
        <div className="py-1.5">
          <div className="px-1.5 space-y-0.5">
            {FIELDS.map((f) => {
              const active = field === f.key
              const handle = () => {
                if (active) {
                  onChange(f.key, direction === "desc" ? "asc" : "desc")
                } else {
                  // New field selection defaults to "desc" — that's what the
                  // user means most of the time ("newest", "highest").
                  onChange(f.key, "desc")
                  setOpen(false)
                }
              }
              return (
                <button
                  key={f.key}
                  type="button"
                  onClick={handle}
                  className={cx(
                    "w-full flex items-center gap-3 p-2 rounded-xl text-left transition-colors duration-100",
                    active ? "bg-emerald-50/70" : "hover:bg-zinc-50",
                  )}
                >
                  <div className="min-w-0 flex-1">
                    <p
                      className={cx(
                        "text-[13px] font-semibold truncate",
                        active ? "text-emerald-900" : "text-zinc-900",
                      )}
                    >
                      {f.label}
                    </p>
                    {active && (
                      <p className="text-[11px] text-emerald-700/70 truncate">
                        {direction === "desc" ? f.descLabel : f.ascLabel} · click to flip
                      </p>
                    )}
                  </div>
                  {active && (
                    <span className="size-6 rounded-full bg-emerald-500 grid place-items-center shrink-0 shadow-sm shadow-emerald-600/30">
                      {direction === "desc" ? (
                        <ChevronDown className="size-3.5 text-white" strokeWidth={3} aria-hidden />
                      ) : (
                        <ChevronUp className="size-3.5 text-white" strokeWidth={3} aria-hidden />
                      )}
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
